import { HttpClient } from '@angular/common/http';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { delay, first, map, Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AppState } from 'src/app/core/app.state';
import { APIResponse } from 'src/app/core/models/api-response';
import { IComposePanel, IDraftPlaceholder } from '../models/compose.models';
import { IDraft, IDraftAttachment, IDraftPayload, IGetDraftsParams } from '../models/draft.models';
import { selectDrafts } from '../store/smart-inbox.selectors';
import { toHttpParams } from '../utils/http-params.util';

/**
 * Handles draft CRUD operations. When `useMockData` is true the NgRx
 * store is used as the persistence layer; otherwise real HTTP calls are made.
 */
@Injectable({ providedIn: 'root' })
export class DraftService {

  private baseUrl = environment.BASE_URL_API + 'inbox/drafts';
  private baseUrlAiDraft = environment.BASE_URL_API + 'inbox/threads';

  /** Toggle to use NgRx store as mock backend. */
  private useMockData = environment.useMockData ?? false;

  private mockIdCounter = 1000;
  private mockAttachmentIdCounter = 10000;

  constructor(
    private http: HttpClient,
    private store: Store<AppState>,
  ) {}

  listDrafts(params: IGetDraftsParams): Observable<APIResponse<IDraft>> {
    const page = params.page ?? 1;
    const limit = params.limit ?? 50;
    const mailboxIds = params.mailbox_ids ?? [];
    const httpParams = toHttpParams({
      page, limit,
      mailbox_ids: mailboxIds,
    });

    if (this.useMockData) {
      return this.store.select(selectDrafts).pipe(
        first(),
        map((drafts) => {
          const filtered = mailboxIds?.length
            ? drafts.filter((d) => mailboxIds.includes(d.mailbox_id))
            : drafts;
          const total = filtered.length;
          const totalPages = Math.max(1, Math.ceil(total / limit));
          const start = (page - 1) * limit;
          const paged = filtered.slice(start, start + limit);
          return {
            message: 'OK',
            data: paged,
            metadata: { page, limit, pages: totalPages, count: total },
          } as APIResponse<IDraft>;
        }),
        delay(300),
      );
    }

    return this.http.get<APIResponse<IDraft>>(this.baseUrl, { params: httpParams });
  }

  getDraft(draftId: number): Observable<APIResponse<IDraft>> {
    if (this.useMockData) {
      return this.store.select(selectDrafts).pipe(
        first(),
        map((drafts) => ({
          message: 'OK',
          data: [],
          object: drafts.find((d) => d.id === draftId),
          metadata: { page: 1, limit: 1, pages: 1, count: 1 },
        } as APIResponse<IDraft>)),
        delay(200),
      );
    }

    return this.http.get<APIResponse<IDraft>>(`${this.baseUrl}/${draftId}`);
  }

  createDraft(panel: IComposePanel): Observable<APIResponse<IDraft>> {
    const payload = this.panelToPayload(panel);

    if (this.useMockData) {
      const now = new Date().toISOString();
      const draft: IDraft = {
        ...payload,
        id: ++this.mockIdCounter,
        user_id: 0,
        is_sent: false,
        is_deleted: false,
        created_at: now,
        updated_at: now,
        attachments: [],
      };
      return of({
        message: 'OK',
        data: [],
        object: draft,
        metadata: { page: 1, limit: 1, pages: 1, count: 1 },
      } as APIResponse<IDraft>).pipe(delay(300));
    }
    return this.http.post<APIResponse<IDraft>>(this.baseUrl, payload);
  }

  updateDraft(draftId: number, panel: IComposePanel): Observable<APIResponse<IDraft>> {
    const payload = this.panelToPayload(panel);

    if (this.useMockData) {
      return this.store.select(selectDrafts).pipe(
        first(),
        map((drafts) => {
          const existing = drafts.find((d) => d.id === draftId);
          const draft = {
            ...(existing ?? { user_id: 0, is_sent: false, is_deleted: false, created_at: null, attachments: [] }),
            ...payload,
            id: draftId,
            updated_at: new Date().toISOString(),
          } as IDraft;
          return {
            message: 'OK',
            data: [],
            object: draft,
            metadata: { page: 1, limit: 1, pages: 1, count: 1 },
          } as APIResponse<IDraft>;
        }),
        delay(300),
      );
    }
    return this.http.patch<APIResponse<IDraft>>(`${this.baseUrl}/${draftId}`, payload);
  }

  deleteDraft(draftId: number): Observable<void> {
    if (this.useMockData) {
      return of(undefined).pipe(delay(300));
    }
    return this.http.delete<void>(`${this.baseUrl}/${draftId}`);
  }

  addDraftAttachment(draftId: number, name: string, contentBase64: string, mimetype?: string, signal?: AbortSignal): Observable<APIResponse<IDraftAttachment>> {
    if (this.useMockData) {
      const att: IDraftAttachment = {
        id: ++this.mockAttachmentIdCounter,
        name,
        file_size: Math.round((contentBase64.length * 3) / 4),
      };
      return of({
        message: 'OK',
        data: [],
        object: att,
        metadata: { page: 1, limit: 1, pages: 1, count: 1 },
      } as APIResponse<IDraftAttachment>).pipe(delay(200));
    }
    const body = { name, content: contentBase64, ...(mimetype && { mimetype }) };
    const context = skipErrorToast();
    const options = signal ? { signal, context } : { context };
    // Angular 14 typings don't include AbortSignal; passing options widens return to HttpEvent. Cast to expected body observable.
    return this.http.post<APIResponse<IDraftAttachment>>(`${this.baseUrl}/${draftId}/attachments`, body, options as any) as unknown as Observable<APIResponse<IDraftAttachment>>;
  }

  removeDraftAttachment(draftId: number, attachmentId: number): Observable<APIResponse<void>> {
    if (this.useMockData) {
      return of({
        message: 'OK',
        data: [],
        metadata: { page: 1, limit: 1, pages: 1, count: 1 },
      } as APIResponse<void>).pipe(delay(200));
    }
    return this.http.delete<APIResponse<void>>(`${this.baseUrl}/${draftId}/attachments/${attachmentId}`);
  }

  private parseEmails(value: string): string[] {
    if (!value?.trim()) return [];
    return value.split(/[,;]/).map((s) => s.trim()).filter(Boolean);
  }

  private panelToPayload(panel: IComposePanel): IDraftPayload {
    return {
      subject: panel.subject,
      body: panel.body,
      body_type: 'html',
      to: this.parseEmails(panel.to),
      cc: this.parseEmails(panel.cc),
      mailbox_id: (panel.from as any)?.id ?? 0,
      thread_id: panel.reply_to_conversation?.id ?? null,
      reply_to_provider_message_id: null,
      type: panel.type,
      minimized: panel.minimized ?? false,
    };
  }

    // ── AI Draft ──────────────────────────────────────────────────────────────

  generateAiDraft(conversationId: number): Observable<APIResponse<{ body: string | null, placeholders: IDraftPlaceholder[], reasoning?: string | null }>> {
    if (this.useMockData) {
      const mockAiDraft = `<p>Hi <[CUSTOMER_NAME]>,</p>
<p>Thank you for reaching out regarding inquiry about our products. We appreciate your business!</p>
<p>I have reviewed your request and here are the details:</p>
<ul>
<li>Product 1 – Quantity: <[STOCK_01]></li>
<li>Product 2 – Quantity: <[STOCK_02]></li>
<li>Product 3 – Quantity: <[STOCK_03]></li>
<li>Product 4 – Quantity: <[STOCK_04]></li>
<li>Product 5 – Quantity: <[STOCK_05]></li>
<li>Product 6 – Quantity: <[STOCK_06]></li>
</ul>
<p>Currently, we are processing your request and will provide you with additional details shortly.</p>
<p>If you have any further questions or need additional information, please feel free to reach out.</p>
<p>Best regards</p>`;

      const body = {
        body: mockAiDraft,
        reasoning: 'The customer is inquiring about the stock of our products.',
        placeholders: [
          {
            id: 'CUSTOMER_NAME',
            description: 'Customer Name',
          },
          {
            id: 'STOCK_01',
            description: 'Stock information for Product 1',
          },
          {
            id: 'STOCK_02',
            description: 'Stock information for Product 2',
          },
          {
            id: 'STOCK_03',
            description: 'Stock information for Product 3',
          },
          {
            id: 'STOCK_04',
            description: 'Stock information for Product 4',
          },
          {
            id: 'STOCK_05',
            description: 'Stock information for Product 5',
          },
          {
            id: 'STOCK_06',
            description: 'Stock information for Product 6',
          },
        ],
        processing_status: 'completed',
        processing_errors: null,
      }

      return of<APIResponse<{ body: string | null, placeholders: IDraftPlaceholder[], reasoning?: string | null }>>({
        data: [],
        object: body,
        message: 'Success',
        metadata: { page: 1, limit: 10, pages: 1, count: 0 },
      }).pipe(delay(1500));
    }

    return this.http.post<APIResponse<{ body: string | null, placeholders: IDraftPlaceholder[], reasoning?: string | null }>>(
      `${this.baseUrlAiDraft}/${conversationId}/ai/draft-reply-enriched`,
      {}
    );
  }
}
